import {
    ChangeDetectionStrategy,
    Component,
    ViewChild,
    inject,
} from '@angular/core';
import {
    DxDataGridComponent,
    DxDataGridModule,
} from 'devextreme-angular/ui/data-grid';
import { DxButtonModule } from 'devextreme-angular/ui/button';
import CustomStore from 'devextreme/data/custom_store';
import { confirm as dxConfirm } from 'devextreme/ui/dialog';
import { Observable } from 'rxjs';
import {
    InvoiceRequestService,
    MessageType,
    NotificationService,
} from '../../../../shared/services';
import {
    CreateInvoiceRequestBody,
    InvoiceRequest,
    PatchInvoiceRequestBody,
} from '../../../../shared/services/invoice-request/interfaces/api';
import { PrintSheetUtils } from '../../../../shared/services/print-sheet/utils';

// A record plus the composed address the grid displays, precomputed so the
// Address column is a plain field rather than a per-evaluation callback.
type InvoiceRequestRow = InvoiceRequest & { addressText: string };

// Admin page for the invoice-request (tax-invoice/bill) API: full CRUD via
// the grid (create/read/update + PDPA erase) plus the lifecycle transitions
// (confirm, mark-issued, cancel). Every action runs from the toolbar over the
// checkbox selection rather than per-row buttons — edit takes exactly one row,
// the rest apply to the whole selection (the API has no bulk endpoints, so
// batches run the per-row calls sequentially).
@Component({
    selector: 'app-manage-request-data',
    templateUrl: './manage-request-data.component.html',
    styleUrls: ['./manage-request-data.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DxDataGridModule, DxButtonModule],
})
export class ManageRequestDataComponent {
    private api = inject(InvoiceRequestService);
    private notification = inject(NotificationService);

    @ViewChild(DxDataGridComponent)
    grid?: DxDataGridComponent;

    readonly platforms = ['shopee', 'lazada', 'tiktokshop', 'web', 'other'];
    readonly sources = ['chatbot', 'web', 'manual'];
    readonly statuses = ['collecting', 'complete', 'issued', 'cancelled'];
    readonly invoiceTypes = ['personal', 'company'];

    refreshButtonOptions = {
        icon: 'refresh',
        hint: 'Reload from server',
        onClick: () => this.grid?.instance.refresh(),
    };

    selectedRowKeys: string[] = [];
    batchRunning = false;
    // Per-action eligible counts within the current selection — drives the
    // batch toolbar buttons (label suffix + disabled state).
    batchCounts = { confirm: 0, markIssued: 0, cancel: 0, erase: 0 };
    // Edit is single-row: the popup edits one record at a time.
    canEditSelection = false;

    // Latest server copy per id — PATCH and the transitions need the current
    // version for If-Match optimistic locking.
    private byId = new Map<string, InvoiceRequest>();

    dataSource = new CustomStore({
        key: 'id',
        load: () =>
            this.api
                .listAll()
                .toPromise()
                .then((items) => {
                    const records = (items ?? []).map(
                        ManageRequestDataComponent.withAddressText,
                    );
                    this.byId = new Map(
                        records.map((record) => [record.id, record]),
                    );
                    // Statuses may have changed server-side while rows stayed
                    // selected — keep the batch button counts honest.
                    this.recomputeBatchCounts();
                    return records;
                }),
        insert: (values: CreateInvoiceRequestBody) =>
            this.api
                .create(values)
                .toPromise()
                .then((created) => {
                    this.toast(
                        `Created ${created?.id} (${created?.status})`,
                        MessageType.success,
                    );
                    return ManageRequestDataComponent.withAddressText(created);
                }),
        update: (key: string, values: PatchInvoiceRequestBody) => {
            const current = this.byId.get(key);
            if (!current) {
                return Promise.reject(
                    new Error('Row is stale — reload and retry.'),
                );
            }
            const patch: PatchInvoiceRequestBody = { ...values };
            // The API forbids patching a `complete` record unless it is
            // explicitly reopened (drops back to `collecting`).
            if (current.status === 'complete') {
                patch.reopen = true;
            }
            return this.api
                .update(key, current.version, patch)
                .toPromise()
                .then((updated) => {
                    this.toast(
                        patch.reopen
                            ? `Updated ${key} — reopened to collecting`
                            : `Updated ${key}`,
                        MessageType.success,
                    );
                    return ManageRequestDataComponent.withAddressText(updated);
                });
        },
        remove: (key: string) =>
            this.api
                .erase(key)
                .toPromise()
                .then(() => {
                    this.toast(`Erased PII of ${key}`, MessageType.success);
                }),
    });

    // Eligibility rules shared by the toolbar buttons and the batch actions.
    private canConfirm = (row: InvoiceRequest) =>
        row.status === 'collecting' && !row.erased;

    private canMarkIssued = (row: InvoiceRequest) => row.status === 'complete';

    private canCancel = (row: InvoiceRequest) =>
        row.status === 'collecting' || row.status === 'complete';

    private canErase = (row: InvoiceRequest) => !row.erased;

    // issued/cancelled records are immutable; erased ones carry no PII to edit
    private canEdit = (row: InvoiceRequest) =>
        !row.erased && this.canCancel(row);

    allowUpdating = (e: { row?: { data?: InvoiceRequest } }) =>
        !!e.row?.data && this.canEdit(e.row.data);

    allowDeleting = (e: { row?: { data?: InvoiceRequest } }) =>
        !!e.row?.data && this.canErase(e.row.data);

    // Editing runs off the selection rather than a per-row button. The popup
    // takes a visible row index, so a row selected on another page (selection
    // spans pages) can't be opened from here.
    editSelected() {
        const rows = this.selectedRequests();
        if (rows.length !== 1 || !this.canEdit(rows[0])) {
            return;
        }
        const rowIndex = this.grid?.instance.getRowIndexByKey(rows[0].id);
        if (rowIndex === undefined || rowIndex < 0) {
            this.toast(
                'The selected row is not on the current page — go to its page and retry.',
                MessageType.error,
            );
            return;
        }
        this.grid?.instance.editRow(rowIndex);
    }

    // Double-click a row to edit it — same eligibility as the toolbar button,
    // and the event already carries the visible row index the popup needs.
    onRowDblClick(e: {
        rowType?: string;
        data?: InvoiceRequest;
        rowIndex?: number;
    }) {
        if (
            e.rowType !== 'data' ||
            !e.data ||
            e.rowIndex === undefined ||
            !this.canEdit(e.data)
        ) {
            return;
        }
        this.grid?.instance.editRow(e.rowIndex);
    }

    // Reads the keys off the event rather than the two-way binding so the
    // counts never lag a click by one change-detection cycle.
    onSelectionChanged(e: { selectedRowKeys?: string[] }) {
        this.selectedRowKeys = e.selectedRowKeys ?? [];
        this.recomputeBatchCounts();
    }

    batchConfirm() {
        return this.runBatch(
            this.canConfirm,
            (row) => this.api.confirm(row.id, row.version),
            'Confirmed',
        );
    }

    batchMarkIssued() {
        return this.runBatch(
            this.canMarkIssued,
            (row) => this.api.markIssued(row.id, row.version),
            'Marked issued',
        );
    }

    batchCancel() {
        return this.runBatch(
            this.canCancel,
            (row) => this.api.cancel(row.id, row.version),
            'Cancelled',
        );
    }

    batchErase() {
        return this.runBatch(
            this.canErase,
            (row) => this.api.erase(row.id),
            'Erased PII for',
            (count) =>
                `PDPA erase: permanently removes all personal data from ` +
                `${count} selected record(s) and keeps anonymized stubs. ` +
                `Continue?`,
        );
    }

    missingFieldsText = (rowData: InvoiceRequest) =>
        (rowData.missingFields ?? []).join(', ');

    // Composed from the structured parts rather than shown as line1 alone:
    // some records carry the whole address in line1 while others split it
    // across sub-district/district/province, so only the composed form reads
    // correctly for both. The individual fields stay editable in the popup.
    //
    // Attached to the row once, rather than computed by the column: a
    // calculateCellValue column is evaluated across the entire store on every
    // sort, search and header-filter, and listAll() can hold thousands of
    // records.
    private static withAddressText(
        record: InvoiceRequest | undefined,
    ): InvoiceRequestRow {
        return {
            ...(record as InvoiceRequest),
            addressText: PrintSheetUtils.constructThaiFullAddress(
                record?.invoice?.address ?? {},
            ),
        };
    }

    onInitNewRow(e: { data: Partial<CreateInvoiceRequestBody> }) {
        e.data.source = 'manual';
        e.data.platform = 'other';
    }

    // storeId/platform/source are create-only — PatchBody rejects them.
    onEditorPreparing(e: any) {
        if (
            e.parentType === 'dataRow' &&
            !e.row?.isNewRow &&
            ['storeId', 'platform', 'source'].includes(e.dataField)
        ) {
            e.editorOptions.disabled = true;
        }
    }

    onRowPrepared(e: any) {
        if (e.rowType === 'data' && e.data?.erased) {
            e.rowElement.classList.add('erased-row');
        }
    }

    private selectedRequests(): InvoiceRequest[] {
        return this.selectedRowKeys
            .map((id) => this.byId.get(id))
            .filter((row): row is InvoiceRequest => !!row);
    }

    private recomputeBatchCounts() {
        const rows = this.selectedRequests();
        this.batchCounts = {
            confirm: rows.filter(this.canConfirm).length,
            markIssued: rows.filter(this.canMarkIssued).length,
            cancel: rows.filter(this.canCancel).length,
            erase: rows.filter(this.canErase).length,
        };
        this.canEditSelection = rows.length === 1 && this.canEdit(rows[0]);
    }

    // Applies `call` to every selected row passing `eligible`, one at a time
    // (keeps If-Match versions simple and avoids hammering the API), then
    // reports a summary toast and reloads the grid.
    private async runBatch(
        eligible: (row: InvoiceRequest) => boolean,
        call: (row: InvoiceRequest) => Observable<unknown>,
        verb: string,
        confirmMessage?: (count: number) => string,
    ) {
        if (this.batchRunning) {
            return;
        }
        const rows = this.selectedRequests().filter(eligible);
        if (rows.length === 0) {
            return;
        }
        if (confirmMessage) {
            const proceed = await dxConfirm(
                confirmMessage(rows.length),
                'Batch PDPA erase',
            );
            if (!proceed) {
                return;
            }
        }
        this.batchRunning = true;
        let done = 0;
        const failures: string[] = [];
        for (const row of rows) {
            try {
                await call(row).toPromise();
                done++;
            } catch (error) {
                failures.push(`${row.id}: ${(error as Error).message}`);
            }
        }
        this.batchRunning = false;
        if (done > 0) {
            this.toast(
                `${verb} ${done} of ${rows.length} selected record(s)`,
                MessageType.success,
            );
        }
        if (failures.length > 0) {
            this.toast(
                `${failures.length} failed — ${failures[0]}` +
                    (failures.length > 1
                        ? ` (and ${failures.length - 1} more)`
                        : ''),
                MessageType.error,
            );
        }
        this.selectedRowKeys = [];
        await this.grid?.instance.refresh();
    }

    private toast(message: string, messageType: MessageType) {
        this.notification.pushMessage({ message, messageType });
    }
}
